import { ConnectionType } from 'modules/core/supabase/tables/match-chronology';
import { useEffect, useState } from 'react';
import supabase from '../../../core/supabase/supabase';
import { analyzeConnectionQuality, ConnectionIndicatorState } from '../../domain/utils/connection-quality-analyzer';

function useOverlayConnectionIndicator(): ConnectionIndicatorState {
  const [status, setStatus] = useState<ConnectionType>(supabase.matchChronology.connectionStatus.value);
  const [prevStatus, setPrevStatus] = useState<ConnectionType | undefined>(undefined);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  const [broadcastStatus, setBroadcastStatus] = useState<ConnectionType>(supabase.broadcast.connectionStatus.value);

  const [pingLatencyMs, setPingLatencyMs] = useState<number | null>(supabase.pingPong.latencyMs.value);
  const [pingIsAlive, setPingIsAlive] = useState<boolean>(supabase.pingPong.isAlive.value);

  const [socketConnected, setSocketConnected] = useState<boolean>(supabase.socketMonitor.socketState.value === 'open');

  useEffect(() => {
    // matchChronology channel status
    const handleStatusChange = (next: ConnectionType, prev: ConnectionType) => {
      setPrevStatus(prev);
      setStatus(next);
    };
    supabase.matchChronology.connectionStatus.subscribe(handleStatusChange);

    // broadcast channel status
    const handleBroadcastStatus = (next: ConnectionType) => setBroadcastStatus(next);
    supabase.broadcast.connectionStatus.subscribe(handleBroadcastStatus);

    // ping-pong
    const handleLatency = (next: number | null) => setPingLatencyMs(next);
    const handleAlive = (next: boolean) => setPingIsAlive(next);
    supabase.pingPong.latencyMs.subscribe(handleLatency);
    supabase.pingPong.isAlive.subscribe(handleAlive);

    // socket monitor
    const handleSocketState = (next: string) => setSocketConnected(next === 'open');
    supabase.socketMonitor.socketState.subscribe(handleSocketState);

    // browser online/offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // start long-running utilities
    supabase.pingPong.start();
    supabase.socketMonitor.start();

    return () => {
      supabase.matchChronology.connectionStatus.unsubscribe(handleStatusChange);
      supabase.broadcast.connectionStatus.unsubscribe(handleBroadcastStatus);
      supabase.pingPong.latencyMs.unsubscribe(handleLatency);
      supabase.pingPong.isAlive.unsubscribe(handleAlive);
      supabase.socketMonitor.socketState.unsubscribe(handleSocketState);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      supabase.pingPong.stop();
      supabase.socketMonitor.stop();
    };
  }, []);

  return analyzeConnectionQuality({
    status,
    prevStatus,
    isOnline,
    broadcastStatus,
    pingLatencyMs,
    pingIsAlive,
    socketConnected,
  });
}

export default useOverlayConnectionIndicator;
